<template>
  <div>
    <SetupPage
        page-label="Psychomotor Skills"
        eyebrow="ASSESSMENT STRUCTURE"
        title="Psychomotor skills"
        description="Maintain the psychomotor skills assessed on report cards — each registered skill becomes an item teachers grade in the psychomotor section."
        toolbar-title="Registered skills"
        :meta="metaLabel"
        :loader="onloader"
    >
        <template slot="side">
            <div v-if="skillitems !== null" class="ws-stat">
                <strong>{{ skills.length }}</strong>
                <span>{{ skills.length === 1 ? "skill" : "skills" }}</span>
            </div>
            <button
                type="button"
                class="btn ws-primary-button"
                title="Add a psychomotor skill"
                @click="openCreate"
            >
                <i class="fa fa-plus" aria-hidden="true"></i>
                <span>New skill</span>
            </button>
        </template>

        <template slot="toolbar">
            <div class="ws-search">
                <i class="fa fa-search" aria-hidden="true"></i>
                <input
                    v-model="searchval"
                    type="search"
                    placeholder="Search skills"
                    aria-label="Search registered psychomotor skills"
                    :disabled="!skills.length"
                />
                <button
                    v-if="searchval"
                    type="button"
                    class="ws-search__clear"
                    aria-label="Clear search"
                    @click="searchval = ''"
                >
                    <i class="fa fa-times" aria-hidden="true"></i>
                </button>
            </div>
        </template>

        <div v-if="skillsLoading && skillitems === null" class="ws-state">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <strong>Loading registered skills</strong>
            <span>Fetching the psychomotor skills set up at your school.</span>
        </div>

        <div v-else-if="skillsFailed" class="ws-state">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
            <strong>Skills could not be loaded</strong>
            <span>Check your connection and try again.</span>
            <button type="button" class="btn ws-ghost-button" @click="retrySkills">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                <span>Try again</span>
            </button>
        </div>

        <div v-else-if="!skills.length" class="ws-state">
            <i class="fa fa-list-ul" aria-hidden="true"></i>
            <strong>No psychomotor skills yet</strong>
            <span>Register the first skill, for example “Handwriting” or “Sports”.</span>
            <button type="button" class="btn ws-ghost-button" @click="openCreate">
                <i class="fa fa-plus" aria-hidden="true"></i>
                <span>Add skill</span>
            </button>
        </div>

        <div v-else-if="!filteredSkills.length" class="ws-state">
            <i class="fa fa-search" aria-hidden="true"></i>
            <strong>No skills match “{{ searchval.trim() }}”</strong>
            <span>Try a different skill name.</span>
            <button type="button" class="btn ws-ghost-button" @click="searchval = ''">
                Clear search
            </button>
        </div>

        <div v-else class="ws-grid" :class="{ 'is-loading': skillsLoading }">
            <article
                v-for="(item, index) in filteredSkills"
                :key="rowKey(item, index)"
                class="ws-card"
            >
                <span class="ws-card__index">{{ index + 1 }}</span>
                <div class="ws-card__body">
                    <span class="ws-card__title">{{ item.Skill || "Untitled skill" }}</span>
                </div>
                <div class="ws-card__actions">
                    <button
                        type="button"
                        class="ws-edit-button"
                        :aria-label="editLabel(item)"
                        @click="editSkill(item)"
                    >
                        <i class="fa fa-pencil" aria-hidden="true"></i>
                        <span>Edit</span>
                    </button>
                    <button
                        type="button"
                        class="ws-danger-button"
                        :aria-label="deleteLabel(item)"
                        @click="deleteSkill(item)"
                    >
                        <i class="fa fa-trash" aria-hidden="true"></i>
                        <span>Delete</span>
                    </button>
                </div>
            </article>
        </div>
    </SetupPage>

    <SetupDrawer
        :show.sync="formVisible"
        icon="fa-hand-paper"
        :eyebrow="editingId ? 'UPDATE RECORD' : 'NEW RECORD'"
        :title="editingId ? 'Edit skill' : 'Add a skill'"
        :subtitle="editingId ? 'Update how this skill appears on report cards.' : 'Add a skill that teachers assess on report cards.'"
        :saving="isSaving"
        :submit-label="editingId ? 'Save changes' : 'Create skill'"
        form-id="skill-form"
        @close="closeForm"
        @submit="submitSkill"
    >
        <fieldset class="ws-fieldset">
            <legend>Skill details</legend>

            <div class="row">
                <div class="col-12">
                    <div class="form-group">
                        <label for="skill-name">
                            Skill name <span class="ws-required">*</span>
                        </label>
                        <input
                            id="skill-name"
                            v-model="form.skillName"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': errors.skillName }"
                            placeholder="e.g. Handwriting"
                            maxlength="80"
                            autocomplete="off"
                            :disabled="isSaving"
                            @input="clearError('skillName')"
                        />
                        <span v-if="errors.skillName" class="ws-error" role="alert">
                            {{ errors.skillName }}
                        </span>
                        <span v-else class="ws-hint">
                            Shown in the psychomotor section of the report card.
                        </span>
                    </div>
                </div>
            </div>
        </fieldset>
    </SetupDrawer>
  </div>
</template>

<script>
import Alert from "@/mixins/Alert.mixins";
import SetupPage from "@/components/SetupPage";
import SetupDrawer from "@/components/SetupDrawer";

const EMPTY_FORM = () => ({
    skillName: ""
});

export default {
    name: "Psycomotor",
    mixins: [Alert],
    components: {
        SetupPage,
        SetupDrawer
    },
    data() {
        return {
            skillitems: null,
            searchval: "",
            skillsLoading: false,
            skillsFailed: false,
            requestSeq: 0,
            formVisible: false,
            isSaving: false,
            editingId: 0,
            form: EMPTY_FORM(),
            errors: {},
            onloader: "none"
        };
    },
    computed: {
        skills() {
            return Array.isArray(this.skillitems) ? this.skillitems : [];
        },
        hasSearch() {
            return this.searchval.trim().length > 0;
        },
        filteredSkills() {
            const tokens = this.searchval
                .toLowerCase()
                .split(/\s+/)
                .filter(Boolean);

            if (!tokens.length) return this.skills;

            return this.skills.filter(item => {
                const haystack = [item && item.Skill]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return tokens.every(token => haystack.includes(token));
            });
        },
        metaLabel() {
            if (this.skillsLoading && this.skillitems === null) {
                return "Loading registered skills…";
            }
            if (this.skillsFailed) return "Registered skills unavailable.";
            if (this.hasSearch) {
                return `${this.filteredSkills.length} of ${this.skills.length} shown`;
            }
            const count = this.skills.length;
            return `${count} ${count === 1 ? "skill" : "skills"} registered`;
        }
    },
    methods: {
        rowKey(item, index) {
            if (item && item.Id !== undefined && item.Id !== null && item.Id !== "") {
                return item.Id;
            }
            return `skill-${index}`;
        },
        editLabel(item) {
            return item && item.Skill ? `Edit ${item.Skill}` : "Edit skill";
        },
        deleteLabel(item) {
            return item && item.Skill ? `Delete ${item.Skill}` : "Delete skill";
        },
        errorMessage(error, fallback) {
            const data = error && error.response && error.response.data;
            if (typeof data === "string" && data.trim()) return data.trim();
            if (data && typeof data === "object") {
                return data.message || data.responseMessage || fallback;
            }
            if (error && error.message) return error.message;
            return fallback;
        },
        async loadSkills() {
            const sequence = ++this.requestSeq;
            this.skillsLoading = true;
            this.skillsFailed = false;

            try {
                const resp = await this.$store.dispatch("getskill");
                if (sequence !== this.requestSeq) return;
                this.skillitems = Array.isArray(resp) ? resp : [];
            } catch (error) {
                if (sequence !== this.requestSeq) return;
                this.skillsFailed = true;
                this.skillitems = [];
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load the registered skills."),
                    ""
                );
            } finally {
                if (sequence === this.requestSeq) this.skillsLoading = false;
            }
        },
        retrySkills() {
            this.skillsFailed = false;
            this.skillitems = null;
            this.loadSkills();
        },
        resetForm() {
            this.editingId = 0;
            this.errors = {};
            this.form = EMPTY_FORM();
        },
        openCreate() {
            this.resetForm();
            this.formVisible = true;
        },
        async editSkill(item) {
            if (!item || item.Id === undefined || item.Id === null) return;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("getskillbyid", item.Id);
                if (!resp) throw new Error("The selected skill could not be loaded.");

                this.resetForm();
                this.editingId = item.Id;
                this.form = {
                    skillName: resp.Skill || item.Skill || ""
                };
                this.formVisible = true;
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load this skill."),
                    ""
                );
            } finally {
                this.onloader = "none";
            }
        },
        closeForm() {
            this.formVisible = false;
            this.resetForm();
        },
        clearError(field) {
            if (!this.errors[field]) return;
            const errors = { ...this.errors };
            delete errors[field];
            this.errors = errors;
        },
        validate() {
            const errors = {};

            if (!String(this.form.skillName || "").trim()) {
                errors.skillName = "Enter the name of the skill.";
            }

            this.errors = errors;

            if (Object.keys(errors).length) {
                const element = document.getElementById("skill-name");
                if (element) element.focus();
            }

            return Object.keys(errors).length === 0;
        },
        async submitSkill() {
            if (this.isSaving) return;
            if (!this.validate()) return;

            const isEdit = Boolean(this.editingId);
            const payload = {
                Id: this.editingId || 1,
                SkillName: String(this.form.skillName || "").trim(),
                processop: isEdit ? "U" : "N",
                username: this.$store.state.auth.username,
                accesskey: this.$store.state.auth.accesskey
            };

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("skillregistration", payload);

                if (resp && resp.responseCode && resp.responseCode !== "00") {
                    this.ShowAlertError(
                        resp.responseMessage || "The skill could not be saved.",
                        ""
                    );
                    return;
                }

                this.closeForm();
                this.searchval = "";
                await this.loadSkills();

                if (this.skillsFailed) {
                    this.ShowAlertWarning(
                        "Skill saved, but the updated list could not be loaded.",
                        ""
                    );
                } else {
                    this.ShowAlertSuccess(
                        isEdit ? "Skill updated successfully." : "Skill created successfully.",
                        ""
                    );
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to save this skill at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        },
        async deleteSkill(item) {
            if (!item || this.isSaving) return;
            if (item.Id === undefined || item.Id === null) return;

            const name = item.Skill || "this skill";
            const result = await this.showSchooldockAlert({
                title: "Delete skill",
                msg: `Delete “${name}”? This cannot be undone.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#c23b4a"
            });
            if (!result || !result.isConfirmed) return;

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("getskillbyid", item.Id);
                const payload = {
                    Id: item.Id,
                    SkillName: (resp && resp.Skill) || item.Skill || "",
                    processop: "D",
                    username: this.$store.state.auth.username,
                    accesskey: this.$store.state.auth.accesskey
                };

                const response = await this.$store.dispatch("skillregistration", payload);

                if (response && response.responseCode && response.responseCode !== "00") {
                    this.ShowAlertError(
                        response.responseMessage || "The skill could not be deleted.",
                        ""
                    );
                    return;
                }

                await this.loadSkills();

                if (this.skillsFailed) {
                    this.ShowAlertWarning(
                        "Skill deleted, but the updated list could not be loaded.",
                        ""
                    );
                } else {
                    this.ShowAlertSuccess("Skill deleted successfully.", "");
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to delete this skill at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        }
    },
    mounted() {
        const userDetails = this.$store.getters["auth/_user"];
        if (!userDetails || userDetails.isSchoolStaff == false) {
            this.$store.dispatch("auth/Logout");
            this.$router.push("/login");
            return;
        }
        this.loadSkills();
    }
};
</script>
